import { create } from 'zustand';
import { useMyPrefsStore } from '@/lib/myPrefs';

/**
 * Persistent network-panel state — proxy MRU history.
 *
 * The list is bounded to {@link MAX_PROXY_HISTORY} entries and de-duplicated
 * by `host:port`. Newest first; when the user picks an old entry it floats
 * back to the top so the most-used presets stay visible.
 *
 * 阶段 A 把 MRU 持久化迁移到 B1 类 `prefs.global.proxy.mru`，跟账号走。
 * `lastUsedMs` 字段在 prefs 里被丢弃（插入顺序已经表达「最新使用」语义），
 * 但仍保留在 in-memory 模型中以便 ProxyCard 未来想加 relative-time 显示。
 */

export interface ProxyHistoryEntry {
  host: string;
  port: number;
  /** Wall-clock ms when the user last applied this entry; drives sort. */
  lastUsedMs: number;
}

const MAX_PROXY_HISTORY = 5;

interface NetworkState {
  proxyHistory: ProxyHistoryEntry[];
  /** Promote / insert an entry; trims to MAX_PROXY_HISTORY newest. */
  rememberProxy: (host: string, port: number) => void;
  forgetProxy: (host: string, port: number) => void;
  clearProxyHistory: () => void;
}

function entriesToMru(entries: ProxyHistoryEntry[]): string[] {
  return entries.map((e) => `${e.host}:${e.port}`);
}

function mruToEntries(mru: string[]): ProxyHistoryEntry[] {
  const out: ProxyHistoryEntry[] = [];
  // Preserve insertion order. lastUsedMs synthesised from index so the
  // sort surface (newest first) keeps working even though we don't
  // round-trip the actual timestamps.
  const now = Date.now();
  for (let i = 0; i < mru.length; i++) {
    const raw = mru[i];
    const colon = raw.lastIndexOf(':');
    if (colon < 1) continue;
    const host = raw.slice(0, colon);
    const port = parseInt(raw.slice(colon + 1), 10);
    if (!host || !Number.isInteger(port) || port < 0 || port > 65535) continue;
    out.push({ host, port, lastUsedMs: now - i });
  }
  return out;
}

function persist(entries: ProxyHistoryEntry[]): void {
  useMyPrefsStore.getState().updateGlobal({ proxy: { mru: entriesToMru(entries) } });
}

export const useNetwork = create<NetworkState>((set) => ({
  proxyHistory: mruToEntries(useMyPrefsStore.getState().global.proxy.mru),
  rememberProxy: (host, port) =>
    set((state) => {
      const filtered = state.proxyHistory.filter(
        (e) => !(e.host === host && e.port === port),
      );
      const next: ProxyHistoryEntry[] = [
        { host, port, lastUsedMs: Date.now() },
        ...filtered,
      ].slice(0, MAX_PROXY_HISTORY);
      persist(next);
      return { proxyHistory: next };
    }),
  forgetProxy: (host, port) =>
    set((state) => {
      const next = state.proxyHistory.filter((e) => !(e.host === host && e.port === port));
      persist(next);
      return { proxyHistory: next };
    }),
  clearProxyHistory: () => {
    persist([]);
    set({ proxyHistory: [] });
  },
}));

// One-way hydrate: stamp in-memory list once prefs finish loading.
if (typeof window !== 'undefined') {
  useMyPrefsStore.subscribe((state, prev) => {
    if (state.ready && !prev.ready) {
      useNetwork.setState({ proxyHistory: mruToEntries(state.global.proxy.mru) });
    }
  });
}
